/**
 * Custom tracking domain — dash data-layer client (FS-674).
 *
 * Thin typed wrapper around `apiClient` for the
 * `/api/v1/custom-tracking-domain` surface. Mirrors the contract in
 * `apps/api/src/routes/custom-tracking-domain.ts`:
 *
 *   GET    /                    → { domains: View[] }
 *   POST   /      { domain }    → { domain: View }
 *   POST   /:id/verify          → { domain: View }
 *   DELETE /:id                 → { deleted: true, id }
 *
 * Keep this module dependency-free beyond `apiClient` — the page component
 * and the unit tests both import from here.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// View shape — matches the API's `CustomTrackingDomainView`.
// ---------------------------------------------------------------------------

export interface CustomTrackingDomainView {
	id: string;
	domain: string;
	verified: boolean;
	active: boolean;
	created_at: number;
	updated_at: number;
}

export interface ListDomainsResponse {
	domains: CustomTrackingDomainView[];
}

export interface CreateDomainResponse {
	domain: CustomTrackingDomainView;
}

export interface VerifyDomainResponse {
	domain: CustomTrackingDomainView;
}

// ---------------------------------------------------------------------------
// FQDN + reserved-suffix rules — kept verbatim in sync with the server-side
// regex in `custom-tracking-domain.ts` so the UI fails fast before POST.
// ---------------------------------------------------------------------------

const FQDN_RE =
	/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

export const RESERVED_SUFFIXES: readonly string[] = ["trackerity.com"];

export function normalizeDomain(input: string): string {
	return String(input ?? "")
		.trim()
		.toLowerCase()
		.replace(/\.$/, "");
}

export function isValidFqdn(domain: string): boolean {
	if (typeof domain !== "string") return false;
	const d = domain.trim();
	if (d.length === 0 || d.length > 253) return false;
	if (d.includes(" ") || d.includes("/") || d.includes(":")) return false;
	if (d.startsWith(".") || d.startsWith("-")) return false;
	const lower = d.toLowerCase().replace(/\.$/, "");
	if (!lower.includes(".")) return false;
	return FQDN_RE.test(lower);
}

export function isReservedDomain(domain: string): boolean {
	const d = normalizeDomain(domain);
	if (d.length === 0) return true;
	for (const suffix of RESERVED_SUFFIXES) {
		if (d === suffix || d.endsWith(`.${suffix}`)) return true;
	}
	return false;
}

// ---------------------------------------------------------------------------
// Add-domain form draft + hand-rolled safeParse surface.
// ---------------------------------------------------------------------------

export interface DomainDraft {
	domain: string;
}

export const EMPTY_DRAFT: DomainDraft = { domain: "" };

export interface ValidatedDomainDraft {
	domain: string;
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const domainDraftSchema = {
	safeParse(input: DomainDraft): ParseResult<ValidatedDomainDraft> {
		const errors: Record<string, string> = {};
		const normalized = normalizeDomain(input.domain);
		if (normalized.length === 0) {
			errors.domain = "Domain is required.";
		} else if (!isValidFqdn(normalized)) {
			errors.domain =
				"Enter a fully-qualified domain (e.g. app.brand.com).";
		} else if (isReservedDomain(normalized)) {
			errors.domain = "That hostname is reserved by the platform.";
		}
		if (Object.keys(errors).length > 0) return { success: false, errors };
		return { success: true, data: { domain: normalized } };
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

/** Emerald chip when verified, zinc chip otherwise — matches the brand. */
export function statusBadgeClass(verified: boolean): string {
	if (verified) {
		return "bg-emerald-50 text-emerald-700 border-emerald-200";
	}
	return "bg-zinc-50 text-zinc-700 border-zinc-200";
}

export function statusLabel(verified: boolean): string {
	return verified ? "Verified" : "Pending";
}

export function formatTimestamp(seconds: number | null | undefined): string {
	if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
		return "—";
	}
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

/** Newest-first, and within the same second deterministically by id. */
export function sortDomains(
	domains: readonly CustomTrackingDomainView[],
): CustomTrackingDomainView[] {
	return domains.slice().sort((a, b) => {
		if (a.created_at !== b.created_at) return b.created_at - a.created_at;
		return a.id.localeCompare(b.id);
	});
}

export function countVerified(
	domains: readonly CustomTrackingDomainView[],
): { total: number; verified: number; pending: number } {
	let verified = 0;
	for (const d of domains) if (d.verified) verified += 1;
	return {
		total: domains.length,
		verified,
		pending: domains.length - verified,
	};
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/custom-tracking-domain";

export async function listDomains(): Promise<CustomTrackingDomainView[]> {
	try {
		const res = await apiClient.get<ListDomainsResponse>(BASE);
		return res.domains ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export async function createDomain(
	payload: ValidatedDomainDraft,
): Promise<CustomTrackingDomainView> {
	const res = await apiClient.post<CreateDomainResponse>(BASE, {
		domain: payload.domain,
	});
	return res.domain;
}

export async function verifyDomain(
	id: string,
): Promise<CustomTrackingDomainView> {
	const res = await apiClient.post<VerifyDomainResponse>(
		`${BASE}/${encodeURIComponent(id)}/verify`,
	);
	return res.domain;
}

export async function deleteDomain(id: string): Promise<void> {
	await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const customTrackingDomainQueryKeys = {
	all: ["custom-tracking-domain"] as const,
	list: () => [...customTrackingDomainQueryKeys.all, "list"] as const,
};

// ---------------------------------------------------------------------------
// Optimistic cache merge helpers
// ---------------------------------------------------------------------------

export function upsertDomain(
	domains: readonly CustomTrackingDomainView[],
	next: CustomTrackingDomainView,
): CustomTrackingDomainView[] {
	const idx = domains.findIndex((d) => d.id === next.id);
	if (idx < 0) return [next, ...domains];
	const out = domains.slice();
	out[idx] = next;
	return out;
}

export function removeDomain(
	domains: readonly CustomTrackingDomainView[],
	id: string,
): CustomTrackingDomainView[] {
	return domains.filter((d) => d.id !== id);
}
